import {AfterViewInit, Component, Input, OnInit, ViewChild} from '@angular/core';
import {MatSort} from '@angular/material/sort';
import {GtManageOrganizationService} from '@app/services/gt-manage-organization.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {BuilderFactory} from '@app/shared/utils/builder.factory';
import {Component as GtComponent, CustomDateEntry, CustomDateModule, ServiceAdministrationService} from '@gt-api';
import * as moment from 'moment';
import {GenericTableRowActions} from '../util/generic-table-row-actions';
import {DateTableEntry, DateTableNewEntry} from './date-table-entry';

@Component({
  selector: 'gt-custom-date',
  templateUrl: './custom-date.component.html',
  styleUrls: ['./custom-date.component.scss']
})
export class CustomDateComponent extends GenericTableRowActions<DateTableEntry, CustomDateEntry, DateTableNewEntry>
  implements OnInit, AfterViewInit {
  @Input() module: CustomDateModule;
  @Input() componentMap: {[key: string]: GtComponent};
  dateDataSourceColumns = ['from', 'to', 'next', 'actions'];
  @ViewChild('sortForDataSource') sortForDataSource: MatSort;
  @Input() moduleName: string;

  constructor(
    private manageOrganizationService: GtManageOrganizationService,
    private serviceAdministrationService: ServiceAdministrationService,
    private notificationService: GtNotificationService
  ) {
    super('dateEntries');
  }

  newTableEntry(entry: CustomDateEntry, editing: boolean, isNew = false, id = -1): DateTableNewEntry {
    const from = moment(entry.from);
    const to = moment(entry.to);
    const builder = BuilderFactory.createBuilder<DateTableNewEntry>();
    builder
      .editing(editing)
      .from(from)
      .to(to)
      .id(id)
      .isNew(isNew)
      .entry(entry)
      .nextCid(entry.nextCid);
    return builder.build();
  }

  newModelEntry(): CustomDateEntry {
    const builder = BuilderFactory.createBuilder<CustomDateEntry>();
    builder
      .from(new Date())
      .to(new Date())
      .nextCid(this.module.legalNextComponents[0].cid);
    return builder.build();
  }

  ngOnInit() {
    if (this.module.legalNextComponents.length <= 1) {
      this.dateDataSourceColumns = ['from', 'to', 'actions'];
    }
    const data = this.module && this.module[this.entriesFieldName] ? this.module[this.entriesFieldName] : [];
    this.dataSource.data = data.map((customDateEntry: CustomDateEntry, i) => this.newTableEntry(customDateEntry, false, false, i));
    this.guiDataSource.data = data.map((customDateEntry: CustomDateEntry, i) => this.newTableEntry(customDateEntry, false, false, i));
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sortForDataSource;
  }

  saveRow(entry: DateTableNewEntry) {
    const start = moment(entry.from);
    const end = moment(entry.to);
    const diff = end.diff(start, 'minutes');
    if (diff < 0) {
      this.notificationService.showErrorCustomMessage('time_router_module.error_negative_duration');
      return;
    } else if (diff === 0) {
      this.notificationService.showErrorCustomMessage('time_router_module.error_start_end_same_time');
      return;
    } else if (this.checkOverlappingTimes(entry)) {
      this.notificationService.showErrorCustomMessage('time_router_module.error_overlapping');
      return;
    }

    entry.entry.from = entry.from.toDate();
    entry.entry.to = entry.to.toDate();
    entry.entry.nextCid = entry.nextCid;
    entry.editing = false;
    if (entry.isNew) {
      delete entry.isNew;
      entry.id = this.dataSource.data.length;
      this.dataSource.data.push(entry);
    } else {
      const backEndEntry = this.dataSource.data.find(existing => {
        return existing.id === entry.id;
      });

      if (backEndEntry) {
        backEndEntry.from = entry.from;
        backEndEntry.to = entry.to;
        backEndEntry.nextCid = entry.nextCid;
      }
    }

    entry.isNew = false;
    const data = this.guiDataSource.data;
    data.sort((a, b) => {
      if (a.isNew) {
        return 1;
      } else {
        return a.from.diff(b.from);
      }
    });

    this.guiDataSource.data = data;

    this.updateComponentEntries();
    this.save();
  }

  deleteRow(row) {
    super.deleteRow(row);
  }

  cancelRow(row: DateTableNewEntry) {
    super.cancelRow(row, row.isNew);
  }

  findSameEntryIndex(row: DateTableNewEntry) {
    return this.dataSource.data.findIndex(r => {
      return r.from.toISOString() === row.from.toISOString() && r.to.toISOString() === row.to.toISOString();
    });
  }

  getModule() {
    return this.module;
  }

  save() {
    this.saveModule(
      this.serviceAdministrationService,
      this.manageOrganizationService.manageOname,
      this.manageOrganizationService.manageLogin
    );
  }

  private checkOverlappingTimes(entry: DateTableEntry): boolean {
    const dateEntries = this.dataSource.data;
    return dateEntries.some(
      existing =>
        existing.id !== entry.id &&
        ((existing.from >= entry.from && existing.from < entry.to) ||
          (existing.to > entry.from && existing.to <= entry.to) ||
          (existing.to >= entry.to && existing.from <= entry.from))
    );
  }
}
